import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'

import CreateOrEditPublicationKeyContactModal from '@modals/CreateOrEditPublicationKeyContactModal'
import EditPublicationDeliverySettingsModal from '@modals/EditPublicationDeliverySettingsModal'
import EditPublicationFileTransferSettingsModal from '@modals/EditPublicationFileTransferSettingsModal'
import ManagePublicationDeadlinesModal from '@modals/ManagePublicationDeadlinesModal'
import ManagePublicationPrintSpecificationsModal from '@modals/ManagePublicationPrintSpecificationsModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'
import UploadPublicationLogoModal from '@modals/UploadPublicationLogoModal'

import PageContent from './components/PageContent'

const callbacks = (component, setState) => {
  const componentCallbacks = {
    CreateOrEditPublicationKeyContactModal: {
      closeModal: () => setState({ showCreateOrEditPublicationKeyContactModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
    EditPublicationDeliverySettingsModal: {
      closeModal: () => setState({ showEditPublicationDeliverySettingsModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
    EditPublicationFileTransferSettingsModal: {
      closeModal: () => setState({ showEditPublicationFileTransferSettingsModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
    ManagePublicationDeadlinesModal: {
      closeModal: () => setState({ showManagePublicationDeadlinesModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
    ManagePublicationPrintSpecificationsModal: {
      closeModal: () => setState({ showManagePublicationPrintSpecificationsModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: payload => handleCallbackAction(payload),
    },
    UploadPublicationLogoModal: {
      closeModal: () => setState({ showUploadPublicationLogoModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showCreateOrEditPublicationKeyContactModal: false,
  showEditPublicationDeliverySettingsModal: false,
  showEditPublicationFileTransferSettingsModal: false,
  showManagePublicationDeadlinesModal: false,
  showManagePublicationPrintSpecificationsModal: false,
  showUploadEntityAssetsModal: false,
  showUploadPublicationLogoModal: false,
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditPublicationKeyContactModal,
    showEditPublicationDeliverySettingsModal,
    showEditPublicationFileTransferSettingsModal,
    showManagePublicationDeadlinesModal,
    showManagePublicationPrintSpecificationsModal,
    showUploadEntityAssetsModal,
    showUploadPublicationLogoModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const { publicationId } = useNumericParams()

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteAttachment: payload => handleCallbackAction(payload),
      showCreateOrEditKeyContactsModal: (payload) => {
        setModalData('CreateOrEditPublicationKeyContactModal', payload)
        setState({ showCreateOrEditPublicationKeyContactModal: true })
      },
      showEditPublicationDeliverySettingsModal: (payload) => {
        setModalData('EditPublicationDeliverySettingsModal', payload)
        setState({ showEditPublicationDeliverySettingsModal: true })
      },
      showEditPublicationFileTransferSettingsModal: (payload) => {
        setModalData('EditPublicationFileTransferSettingsModal', payload)
        setState({ showEditPublicationFileTransferSettingsModal: true })
      },
      showManagePublicationDeadlinesModal: (payload) => {
        setModalData('ManagePublicationDeadlinesModal', payload)
        setState({ showManagePublicationDeadlinesModal: true })
      },
      showManagePublicationPrintSpecificationsModal: (payload) => {
        setModalData('ManagePublicationPrintSpecificationsModal', payload)
        setState({ showManagePublicationPrintSpecificationsModal: true })
      },
      showUploadEntityAssetsModal: (payload) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
      showUploadPublicationLogoModal: (payload) => {
        setModalData('UploadPublicationLogoModal', payload)
        setState({ showUploadPublicationLogoModal: true })
      },
      updatePublication: payload => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent publicationId={publicationId} />

        <CreateOrEditPublicationKeyContactModal
          callbacks={callbacks('CreateOrEditPublicationKeyContactModal', setState)}
          showModal={showCreateOrEditPublicationKeyContactModal}
        />

        <EditPublicationDeliverySettingsModal
          callbacks={callbacks('EditPublicationDeliverySettingsModal', setState)}
          showModal={showEditPublicationDeliverySettingsModal}
        />

        <EditPublicationFileTransferSettingsModal
          callbacks={callbacks('EditPublicationFileTransferSettingsModal', setState)}
          showModal={showEditPublicationFileTransferSettingsModal}
        />

        <ManagePublicationDeadlinesModal
          callbacks={callbacks('ManagePublicationDeadlinesModal', setState)}
          showModal={showManagePublicationDeadlinesModal}
        />

        <ManagePublicationPrintSpecificationsModal
          callbacks={callbacks('ManagePublicationPrintSpecificationsModal', setState)}
          showModal={showManagePublicationPrintSpecificationsModal}
        />

        <UploadEntityAssetsModal
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />

        <UploadPublicationLogoModal
          callbacks={callbacks('UploadPublicationLogoModal', setState)}
          showModal={showUploadPublicationLogoModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
